import { useRef, useEffect } from "react";

interface AutoScrollImageProps {
  src: string;
  alt: string;
  crop?: "center" | "podcast" | "youtube";
  speed?: number;
}

export default function AutoScrollImage({ src, alt, crop = "center", speed = 40 }: AutoScrollImageProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const isUserScrolling = useRef(false);
  const lastTouchY = useRef(0);

  useEffect(() => {
    const container = containerRef.current;
    const img = imgRef.current;
    if (!container || !img) return;

    let frame: number;
    let direction = 1;
    let scroll = 0;
    let maxScroll = 0;
    let animationActive = true;

    const updateMaxScroll = () => {
      maxScroll = img.offsetHeight - container.offsetHeight;
      if (maxScroll < 0) maxScroll = 0;
    };

    const animate = () => {
      if (!animationActive || isUserScrolling.current) return;
      updateMaxScroll();
      scroll += direction * (speed / 60); // px per frame
      if (scroll >= maxScroll) {
        scroll = maxScroll;
        direction = -1;
      } else if (scroll <= 0) {
        scroll = 0;
        direction = 1;
      }
      container.scrollTop = scroll;
      frame = requestAnimationFrame(animate);
    };

    const handleTouchStart = (e: TouchEvent) => {
      isUserScrolling.current = true;
      lastTouchY.current = e.touches[0].clientY;
    };

    const handleTouchEnd = () => {
      isUserScrolling.current = false;
      // Resume animation after a short delay
      setTimeout(() => {
        frame = requestAnimationFrame(animate);
      }, 1000);
    };

    frame = requestAnimationFrame(animate);
    window.addEventListener("resize", updateMaxScroll);
    container.addEventListener("touchstart", handleTouchStart);
    container.addEventListener("touchend", handleTouchEnd);

    return () => {
      animationActive = false;
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", updateMaxScroll);
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchend", handleTouchEnd);
    };
  }, [speed]);

  // Crop style
  let objectPosition = "center top";
  if (crop === "podcast") objectPosition = "60% top";
  if (crop === "youtube") objectPosition = "center top";

  return (
    <div
      ref={containerRef}
      className="relative aspect-video w-full overflow-hidden rounded-2xl flex items-start justify-center bg-[#111]"
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        width={400}
        height={800}
        style={{ objectFit: "cover", objectPosition, minWidth: "100%", pointerEvents: "none", userSelect: "none" }}
        className="select-none"
        draggable={false}
      />
    </div>
  );
}